import React, { useCallback } from 'react';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { Field, NoSQLCollection } from '@nexla/@types';
import { tryCatch } from 'new/utils/TryCatch';
import { Stack } from 'new/elements/Stack';
import useApp from 'new/state/app/repository/useApp';
import useProbe from 'new/state/probe/repository/useProbe';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import usePaging from 'new/state/paging/repository/usePaging';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import useProbeAdapter from 'new/state/probe/adapter/useProbeAdapter';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import { useGetMetaInfoByConnectorType } from 'new/hooks/useGetMetaInfoByConnectorType';
import { getMonitoringIntervalConfig } from 'new/components/flows/types/DirectFlow/utils';
import NoSQLSourceConfigurator from 'js/components/data_sources/add_ds_form/database/NoSQLConfigurator';
import NoSQLSinkConfigurator from 'js/components/destination/destination-content/database/NoSQLConfigurator';
import { PROBE_GET_SAMPLE_REQUEST, PROBE_GET_TREE_REQUEST } from 'js/constants/actionTypes';
import { loadDBDataTypes, probeGetInitialDBTree, probeGetSample } from 'js/actions';
import {
  getNoSQLCollectionConfig,
  validateNoSQLSourceConfig,
} from 'new/components/flows/configure/NoSQL/NoSQL.utils';
import { useHistory } from 'react-router-dom';
import useFlowParams from '../../useFlowParams';

type NoSQLProps = {
  formType: string;
  flowType: string;
  selectedConnection: string;
  selectedBucket: any;
  selectedCredential: any;
  /**
   * Optional values
   */
  sink?: any;
  nexset?: any;
  nexsetSchema?: any;
  sinkConfig?: any;
  sourceConfig?: any;
  onSourceConfigChanged?(data: any): void;
  onSinkConfigChanged?(sinkConfig: any, field: any, value: any): void;
  onSaveAdvancedMode(data: any): void;
  nextFormStage(...args: any): any;
  onError?(msg: string): void;
};

export const NoSQL = (props: NoSQLProps) => {
  const {
    sink,
    nexset,
    nexsetSchema,
    formType,
    flowType,
    selectedBucket,
    selectedConnection,
    selectedCredential,
    sinkConfig,
    sourceConfig,
    onSinkConfigChanged,
    onSourceConfigChanged,
  } = props;

  const ctx = useApp();
  const org = useCurrentOrg();
  const history = useHistory();
  const { paging } = usePaging();
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const dbDataTypes = useSelector((s: any) => s.dbDataTypes);
  const { getProbeData } = useProbe();
  const { requestConnectionTree } = useProbeAdapter();
  const { data, updateFormData } = useFlowsSetup(formType, flowType);
  const meta = useGetMetaInfoByConnectorType(selectedConnection);
  const isLoadingProbeData = useIsLoading(PROBE_GET_TREE_REQUEST);
  const isLoadingProbeSample = useIsLoading(PROBE_GET_SAMPLE_REQUEST);
  const { isModal, onDismiss } = useFlowParams();
  const isSource = data.connectionType === 'SOURCE';
  const credID = selectedCredential?.id || data?.credentialId || 0;

  const probe = getProbeData(credID);
  const fields = paging.sourceConfigById[selectedConnection] as Field[];
  const intervalConfig = getMonitoringIntervalConfig(sourceConfig, fields);

  const onMetaInfoChanged = (name: string, value: string) => {
    const key = (() => {
      if (name === 'name') return 'sourceName';
      if (name === 'description') return 'sourceDescription';
      return name;
    })();
    updateFormData({ sourceConfig: { ...sourceConfig, [key]: value } });
  };

  const onCollectionSelected = (collection: NoSQLCollection, isNew = false) => {
    if (!collection) return;
    const prefix = isSource ? 'sourceConfig' : 'sinkConfig';
    const config = isSource ? sourceConfig : sinkConfig;
    const formData = getNoSQLCollectionConfig(collection, config, prefix);
    updateFormData({ ...formData, isNewCollection: isNew });
  };

  const onRequestProbeTree = (isRoot = true, folder = null) => {
    const { error } = tryCatch(() => requestConnectionTree(credID, isRoot, folder));
    if (error) console.warn(error.message);
  };

  const onFetchSamples = () => {
    const { error, message } = validateNoSQLSourceConfig(sourceConfig, fields);
    if (error) {
      console.error('NoSQL.onFetchSamples: ', message);
      return;
    }
    const probeConfig = { ...sourceConfig };
    if (probeConfig.mode === 'default') {
      delete probeConfig.mode;
    }
    dispatch(probeGetSample(credID, 0, probeConfig));
  };

  const onRequestInitialDBProbeTree = useCallback(
    (folder: any) => dispatch(probeGetInitialDBTree(credID, true, folder)),
    [credID],
  );

  const goBack = useCallback(() => {
    if (isModal) {
      onDismiss?.({});
      return;
    }
    history.goBack();
  }, [isModal, onDismiss, history]);

  if (isSource) {
    if (!meta || !sourceConfig || !probe) {
      return (
        <Stack padding="50px">
          <CenteredLoader />
        </Stack>
      );
    }

    return (
      <NoSQLSourceConfigurator
        sourceBeingEdited={data?.source}
        name={sourceConfig?.sourceName}
        description={sourceConfig?.sourceDescription}
        selectedConnection={selectedConnection}
        connectionMetas={paging.metaInfoById}
        sourceConfigFields={paging.sourceConfigById}
        currentOrg={org}
        sourceConfig={sourceConfig}
        probeSample={probe}
        selectedCredentialId={credID}
        visibleFieldData={intervalConfig}
        probeData={probe.data}
        probeError={probe.error}
        probeDataFlat={probe.flatData}
        isLoadingProbeData={isLoadingProbeData}
        isLoadingProbeSample={isLoadingProbeSample}
        selectedCollection={data?.selectedCollection}
        selectedDatabase={(selectedBucket || {})?.database}
        // actions
        onGetProbeSample={onFetchSamples}
        onMetaInfoChanged={onMetaInfoChanged}
        onRequestProbeTree={onRequestProbeTree}
        onCollectionSelected={onCollectionSelected}
        onSourceConfigChanged={onSourceConfigChanged}
        onRequestInitialDBProbeTree={onRequestInitialDBProbeTree}
        setContextualSupport={ctx.setContextualSupport}
        unsetContextualSupport={ctx.unsetContextualSupport}
        onSaveAdvancedMode={props?.onSaveAdvancedMode}
        nextFormStage={props?.nextFormStage}
        goBack={goBack}
        onError={props?.onError}
      />
    );
  }

  if (!meta || !sinkConfig || !probe) {
    return (
      <Stack padding="50px">
        <CenteredLoader />
      </Stack>
    );
  }

  const nexsetID = nexset?.id;
  const allDBDataTypes = dbDataTypes;
  const { schemaToDBDataTypes } = allDBDataTypes[selectedConnection] || {};

  const onFetchDBDataTypes = (connectionType: string) => dispatch(loadDBDataTypes(connectionType));

  const onToggleExcludeAttr = (attrName: string) => {
    const excludeByAttrName = data.excludeByAttrName || {};
    updateFormData({
      excludeByAttrName: {
        ...excludeByAttrName,
        [attrName]: !excludeByAttrName[attrName],
      },
    });
  };

  const onUpdateOrderedDataSchema = (attrs: any) => updateFormData({ orderedOutputSchema: attrs });

  const noSQLUpdateFormData = (_: any, formData: any) => updateFormData(formData);

  return (
    <NoSQLSinkConfigurator
      type="format"
      dataSet={nexset}
      formType={formType}
      dataSetId={nexsetID}
      addedDataSink={sink}
      isEditMode={sink?.id}
      probeSample={probe}
      probeData={probe.data}
      probeError={probe.error}
      probeDataFlat={probe.flatData}
      isNewCollection={data?.isNewCollection}
      selectedCollection={data?.selectedCollection}
      isLoadingProbeData={isLoadingProbeData}
      isLoadingProbeSample={isLoadingProbeSample}
      connectionMetas={paging.metaInfoById}
      sinkConfig={sinkConfig}
      sinkConfigFields={paging.sinkConfigById}
      selectedCredentialId={credID}
      selectedBucket={selectedBucket}
      selectedConnection={selectedConnection}
      selectedDatabase={(selectedBucket || {}).database}
      dataSchema={nexsetSchema}
      dbDataTypes={allDBDataTypes}
      orderedDataSchema={data?.orderedOutputSchema}
      schemaToDBDataTypes={schemaToDBDataTypes}
      excludeByAttrName={data?.excludeByAttrName}
      // actions
      updateFormData={noSQLUpdateFormData}
      onGetProbeSample={onFetchSamples}
      loadDBDataTypes={onFetchDBDataTypes}
      onToggleExclude={onToggleExcludeAttr}
      onMetaInfoChanged={onMetaInfoChanged}
      onRequestProbeTree={onRequestProbeTree}
      onSinkConfigChanged={onSinkConfigChanged}
      onCollectionSelected={onCollectionSelected}
      onSourceConfigChanged={onSourceConfigChanged}
      updateOrderedDataSchema={onUpdateOrderedDataSchema}
      onRequestInitialDBProbeTree={onRequestInitialDBProbeTree}
      setContextualSupport={ctx.setContextualSupport}
      unsetContextualSupport={ctx.unsetContextualSupport}
      onSaveAdvancedMode={props?.onSaveAdvancedMode}
      nextFormStage={props?.nextFormStage}
      goBack={goBack}
      onError={props?.onError}
    />
  );
};
